/* @flow */

import TrackingUtil from "../../utils/TrackingUtil";
import { FsdAddressType } from "./FsdAddress";
import { CustomerId } from "./CustomerId";

/**
 * The json as it comes from the customer service + a few utility methods.
 * NOTE: We only parse the data we really need. If you need more attributes, this is the place to parse them.
 */
export type Customer = {
    customerId: CustomerId;
    displayNumber: string;
    homeStore: string;
    mainPhone: string | undefined | null;
    mobilePhone: string | undefined | null;
    commercialName: string | undefined | null;
    email: string | undefined | null;
    food: boolean | undefined | null;
    chamberOfCommerceNumber: string | undefined | null;
    metroContractNumber: string | undefined | null;
    currency: string | undefined | null;
    addresses: Array<FsdAddressType> | undefined | null;
    deliveryAddresses: Array<FsdAddressType>;
    documentHistory:
        | {
              isAdminCustomer: boolean;
              documentName?: string;
              documentType?: number;
          }
        | undefined
        | null;
    owner:
        | {
              firstName?: string;
              lastName?: string;
              salutation?: string;
          }
        | undefined
        | null;
    customerName: string;
    customerNumber: string;
    payments: Array<any> | undefined | null;
    deliveryOpts: {
        [x: string]: any;
    };
    countryCodeAlpha: string;
    currentCardholders: Array<any>;
    webShopAddresses: Array<any> | undefined | null;
    fulfillmentOpts: {
        [x: string]: any;
    };
    primaryCardholderNumber: string;
    primaryPaymentAllowance: string | undefined | null;
    valid: boolean;
    validForFsd: boolean;
    validForWeb: boolean;
    targetGroup: string | undefined | null;
    categoryGroups: Array<string> | undefined | null;
    deliveryStores: Array<string> | undefined | null;
    segment: string | undefined | null;
    isTraderFranchise: boolean;
};

export function transformCustomer(response: any): Customer {
    const traderFranchiseTag = "TRADER_FRANCHISE";
    const deliveryAddresses = response.addresses
        ? response.addresses.filter((item: { [x: string]: any }) => {
              return item.addressType === "DELIVERY" && item.deliveryStore;
          })
        : [];
    return {
        ...response,
        ...{
            deliveryAddresses: deliveryAddresses,
            commercialName: response.commercialName1,
            customerName: response.customerName1,
            targetGroup: response.branchCode,
            categoryGroups: response.categoryGroupCodes,
            isTraderFranchise: response.tags
                ? response.tags.some((tagItem: string) => {
                      return tagItem === traderFranchiseTag;
                  })
                : false
        }
    };
}

export function extractCardholder(
    customer: Customer,
    cardHolderNum: string | undefined | null
):
    | {
          [x: string]: any;
      }
    | undefined
    | null {
    if (cardHolderNum) {
        const result = customer.currentCardholders.find(({ cardHolderNumber }) => cardHolderNumber === cardHolderNum);
        if (!result) {
            const message =
                "Cardholder number was not found in currentCardholders! Requested number is: " + cardHolderNum;
            TrackingUtil.trackUiErrorCa("Customer", message, new Error());
        }
        return result;
    } else {
        const message = "Cardholder number is not specified! Will return first cardholder in list.";
        TrackingUtil.trackUiErrorCa("Customer", message);
        return customer.currentCardholders[0];
    }
}

export function extractPrimaryPerson(customer: Customer) {
    return extractCardholder(customer, customer.primaryCardholderNumber);
}

export default Customer;
